import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Payment } from 'types/Payment';
import useItem from 'common/hooks/useItem';
import DetailHeader from 'common/components/DetailHeader/DetailHeader';
import dayjs from 'dayjs';
import Status from 'common/components/Status/Status';
import Icon from 'components/Icon/Icon';
import ProfileCard from 'components/ProfileCard/ProfileCard';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import AlertsContext from 'common/providers/alerts';
import formatPrice from 'utils/formatPrice';

function PaymentDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { setAlert } = useContext(AlertsContext);

  const { item, error } = useItem<Payment>('payments', id as string, {
    populate: ['profile', 'profile.avatar', 'profile.companyLogo', 'user', 'invoice'],
  });

  const {
    profile,
    description,
    amount,
    executionDate,
    effectiveStartDate,
    effectiveEndDate,
    invoice,
  } = (item as Payment) || {};

  const handleAuthorClick = () => {
    if (id) {
      navigate(`/profiles/edit/${id}`);
    }
  };

  const formatDate = (date: string) => {
    return dayjs(date).format('DD/MM/YYYY');
  };

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const PublicationHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center text-xs gap-1 field-title">
          <Icon name="ClockIcon" className="w-4 h-4" />
          {`${dayjs(item?.executionDate).fromNow()}`}
        </div>
        <div className="responsive-flex items-center gap-3">
          <div className="flex flex-row items-center text-ternary-800">
            <div className="text-sm flex">
              <div className="flex items-center">
                <span>
                  {t('common.paymentExecutedAt', {
                    at: dayjs(item?.executionDate).format('LL'),
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    [item?.executionDate, t]
  );

  const PublicationHeaderSubTitle = useCallback(
    (mainText: string, subText?: string) => (
      <>
        <span className="font-bold">{mainText}</span>
        {subText && <span> | {subText}</span>}
      </>
    ),
    []
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <DetailHeader
        backLink="/payments"
        title={`${item?.executionDate ? dayjs(item.executionDate).format('DD/MM/YYYY') : ''}`}
        Status={
          <div className="flex gap-2 flex-wrap">
            <Status status={item?.status} />
          </div>
        }
        subtitleIcon="Payment"
        TitleIcon={
          <div>
            <Icon name="Payment" className="w-10 h-10" />
          </div>
        }
        HeaderInfos={PublicationHeaderInfos}
        SubTitle={PublicationHeaderSubTitle(t('common.payment'))}
      />

      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {/* Description */}
            <div className="responsive-flex flex flex-col xl:flex-row gap-2 items-start">
              {/* Column 1 */}
              <div className="responsive-flex gap-2 flex-col w-full xl:w-2/3">
                <div className="white-box flex gap-2 flex-col w-full">
                  <SectionTitle
                    title={t('common.paymentInfo')}
                    icon="CreditCardIcon"
                    className="text-secondary mt-6"
                  />
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t('common.paymentExecutedAt', {
                          at: '',
                        })}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {executionDate ? formatDate(executionDate) : t('status.notAvailable')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t('common.amount')}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{formatPrice(amount / 100)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        {t('forms.description')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {description || t('status.notAvailable')}
                      </dd>
                    </div>
                    {effectiveStartDate && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {t('common.effectivePeriod')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {formatDate(effectiveStartDate)} -{' '}
                          {effectiveEndDate ? formatDate(effectiveEndDate) : t('status.ongoing')}
                        </dd>
                      </div>
                    )}
                    {item?.status === 'error' && item?.errorMessage && (
                      <div className="flex flex-col">
                        <div className="flex flex-row items-center gap-2 text-lg text-red-600 mt-2">
                          {t('common.echecPayment')}
                        </div>
                        <div>
                          <dd className="mt-1">
                            <p className="mt-1 text-sm text-red-600">{item?.errorMessage}</p>
                          </dd>
                        </div>
                      </div>
                    )}
                  </dl>
                  {invoice && (
                    <div className="px-4 py-3 text-right sm:px-6">
                      <a
                        href={`${import.meta.env.VITE_API_URL}${invoice?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-row"
                      >
                        <div className="flex flex-row items-center mt-1 -ml-0.5 gap-2 text-blue-600 border border-blue-600 bg-blue-50 rounded-full px-2 hover:bg-slate-200">
                          <Icon name="DownloadFile" className="w-4 h-4" />
                          {t('common.invoice')}
                        </div>
                      </a>
                    </div>
                  )}
                </div>
              </div>
              {/* Column 2 */}
              <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
                <ProfileCard
                  profile={profile}
                  title={t('forms.author')}
                  onClick={handleAuthorClick}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentDetails;
